// Private search: runs over decrypted history on this device; the server never sees it.
// Libraries and their files are searched the same way (./librarySearch.ts).
import type { StoredMessage } from '../storage/db'

export interface SearchHit {
  msg: StoredMessage
  /** Text around the first match. */
  snippet: string
}

function textOf(m: StoredMessage): string | null {
  const e = m.envelope
  if (e.type === 'text') return e.body
  if (e.type === 'media') return [e.media.name, e.body].filter(Boolean).join(' — ')
  if (e.type === 'library') return [e.item.title, e.item.artist, e.body].filter(Boolean).join(' — ')
  return null
}

/** Lower case without accents, so "cafe" finds "Café". */
export const fold = (s: string) => s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase()

/** Case- and accent-insensitive search; newest first; edits applied, deleted skipped. */
export function search(
  byConv: Record<string, StoredMessage[]>,
  query: string,
  edits: (conv: string) => Map<string, { body: string | null; deleted: boolean }>,
  limit = 50,
): SearchHit[] {
  const q = fold(query.trim())
  if (q.length < 2) return []
  const hits: SearchHit[] = []
  for (const [conv, msgs] of Object.entries(byConv)) {
    const ann = edits(conv)
    for (const m of msgs) {
      const st = ann.get(m.envelope.id)
      if (st?.deleted) continue
      const text = st?.body ?? textOf(m)
      if (!text) continue
      const at = fold(text).indexOf(q)
      if (at < 0) continue
      const start = Math.max(0, at - 30)
      hits.push({ msg: m, snippet: (start > 0 ? '…' : '') + text.slice(start, at + q.length + 60) })
    }
  }
  hits.sort((a, b) => (b.msg.envelope.ts || b.msg.receivedAt) - (a.msg.envelope.ts || a.msg.receivedAt))
  return hits.slice(0, limit)
}
